import { sunboundPayProxy } from '@proxies';
import React, { useEffect, useState, useMemo } from "react";
import { Toggle } from "@common";
import { useSpinnerContext } from '@contexts';
import { PaymentDay } from ".";
import { useLocation, useNavigate } from 'react-router-dom';
import { Constants, helpers, Enums } from "@shared";
import { useSessionContext } from '@contexts';
import { PaymentMethod } from "./shared/types";
import { PaymentMethodCard } from "./common/payment-method-card";

const PaymentMethodSettings = () => {

    const navigate = useNavigate(), location = useLocation();
    const [settings, setSettings] = useState(null);
    const { setSpinnerShow } = useSpinnerContext();

    const {
        session,
        accounts,
        selectedAccount
    } = useSessionContext();

    const {
        orgDays,
        idAccount,
        potDeadline
    } = session;

    const hasMultipleAccounts = accounts.length > 1;

    useEffect(() => {

        (async () => {
            setSpinnerShow(true);
            try {
                const settings = await sunboundPayProxy.getPaymentSettings(idAccount);
                setSettings(settings);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, [idAccount]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggleAutoPay = async () => {
        setSpinnerShow(true);
        try {
            let { dueDay } = settings;
            const { isAutoPay } = settings;
            const flipAutoPay = !isAutoPay;

            if (flipAutoPay) {
                ({ autodrawDay: dueDay } = orgDays);
            } else
                dueDay = null;

            await sunboundPayProxy.updateAccountPayorPreferences(idAccount, { isAutoPay: flipAutoPay, dueDay });
            setSettings({ ...settings, isAutoPay: flipAutoPay, dueDay });

        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const dueDayChange = async (dueDay) => {
        setSpinnerShow(true);
        try {
            await sunboundPayProxy.updateAccountPayorPreferences(idAccount, { dueDay });
            setSettings({ ...settings, dueDay });
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const resolveDefaults = helpers.shouldResolveDefaults(accounts, selectedAccount);

    const addDefaultPaymentMethod = () => {
        if (location.state?.onClickPaymentFlow) {
            // in the case where we're resolving a disabled payment
            // we want to use the autopay flow here not just addDefault
            helpers.navigateStart(location, navigate, ...location.state.onClickPaymentFlow);
        } else {
            helpers.navigateStart(location, navigate, Constants.Flows.addDefaultPaymentMethod.id, {
                resolveDefaults
            }, {
                account: { ...session },
                hasMultipleAccounts: false,
            });
        }
    };

    const makeDefault = async (idPaymentMethod) => {
        setSpinnerShow(true);
        try {
            await sunboundPayProxy.upsertAccountDefaultPaymentMethod(idAccount, idPaymentMethod);
            const settings = await sunboundPayProxy.getPaymentSettings(idAccount);
            setSettings(settings);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    // find the defaultPM for the currently selected account
    const defaultPM: PaymentMethod | null = useMemo(() => {
        return settings?.paymentMethods?.find(pm =>
            pm.accountPaymentMethods?.some(apm => apm.isDefault && apm.accountPayor?.account?.idAccount === idAccount)
        ) ?? null;
    }, [settings, idAccount]);

    const manageMultiAutopay = () => {
        helpers.navigateStart(location, navigate, Constants.Flows.multiAutopay.id);
    };

    const autopay = useMemo(() => {
        const { isAutoPay = false } = settings || {};
        if (hasMultipleAccounts)
            return (<button type="button" className="btn btn-outline-primary border-2 p-1 px-2 text-blue-on" onClick={manageMultiAutopay}>Manage Auto-Pay</button>);

        return <Toggle isOn={isAutoPay} onClick={toggleAutoPay} enabled={(defaultPM && !defaultPM?.disabledAt) || isAutoPay} />;
    }, [settings]);

    const paymentDay = useMemo(() => {
        const { isAutoPay = false, dueDay: payorDueDay = null, hasPOTInvoice } = settings || {};
        const { locked, autodrawDay, maxDay } = helpers.getDaysFromPayor(orgDays, payorDueDay, hasPOTInvoice ? Enums.ProductType.SunboundPay : null, potDeadline);

        return (
            settings && isAutoPay && !hasMultipleAccounts &&
            <PaymentDay setDueDay={dueDayChange} dueDay={autodrawDay} locked={locked} maxDay={maxDay} />
        );

    }, [settings]);

    // sort disabled PMs to the bottom of the list
    const sortedPaymentMethods = useMemo(() => [...(settings?.paymentMethods ?? [])].sort((a: PaymentMethod, b: PaymentMethod) => {
        if (a.disabledAt && !b.disabledAt) return 1;
        if (!a.disabledAt && b.disabledAt) return -1;
        return 0;
    }), [settings?.paymentMethods]);

    if (!settings) return null;

    return (
        <div className="page-content-wrapper">
            <div className="content-box items-start shadow-sb">
                <div className="flex flex-col gap-y-6">
                    <div className="flex flex-col gap-y-5 w-full">
                        <span className="font-bold text-2xl">Payment Methods</span>
                        <div className="text-l text-gray-500">
                          Add and set a default payment method for your account.
                        </div>
                        {
                            sortedPaymentMethods.length > 0
                                ? sortedPaymentMethods.map((pm, idx) =>
                                    <PaymentMethodCard
                                        idx={idx}
                                        key={pm.idPaymentMethod}
                                        paymentMethod={pm}
                                        isDefault={(pm && defaultPM && pm.idPaymentMethod === defaultPM.idPaymentMethod)}
                                        makeDefault={makeDefault} />)
                                : <span>No registered payment methods</span>
                        }
                        <button type="button" className="btn btn-primary text-lg rounded-full" onClick={addDefaultPaymentMethod}>Add Payment Method</button>
                    </div>
                    <div className="w-full border-t-2 border-gray-300"></div>
                    <div className="flex flex-col gap-y-2">
                        <div className="flex flex-row justify-between">
                            <div>
                                <span className="font-bold text-2xl">Auto-pay</span>
                            </div>
                            <div>
                                {autopay}
                            </div>
                        </div>
                        {
                            settings.isAutoPay
                            ? <>
                                {
                                    defaultPM?.disabledAt &&
                                    <span className="text-red-500 text-lg">Auto-pay will resume when a new default payment method is set</span>
                                }
                            </>
                            : <>
                                {
                                    (!defaultPM || defaultPM.disabledAt) && !hasMultipleAccounts &&
                                    <span className="text-red-500 text-lg">Auto-pay will become available after you add a default payment method</span>
                                }
                                {
                                    defaultPM?.disabledAt &&
                                    <span className="text-red-500 text-lg">Auto-pay will become available when a new default payment method is set</span>
                                }
                            </>
                        }
                        <span className="text-gray-500">
                            We’ll pay your full balance to Sunbound using your Default Payment Method on your payment day.
                        </span>
                    </div>
                    <div>
                        {paymentDay}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentMethodSettings;
